<template>

	<div>

		<Modal 
			:noModal="noModal"
			:class="{ keywords: true }"
			:width="(!csvUploaded&&!noModal)?700:0"
			:title="showTitle"
			:loading="loading"
			@close="close"
		>

			<template v-slot:content>
			
				<p v-if="confirmed&&hasProject" class="confirmed">
							
					Thanks, your CSV is now queued for processing, we'll notify you by email when it's complete.

				</p>

				<p v-if="confirmed&&!hasProject" class="confirmed">
					
					Thanks, your CSV is now queued for processing (this will start once you have finished creating your project), we'll notify you by email when it's complete.

				</p>

				<form class="form" @submit.prevent="submit" v-if="!csvUploaded&&!confirmed">

					<label>Frequency</label>
					<p class="info" v-html="frequencyLabel" v-if="frequencyLabel" />
					<v-select 
						v-model="frequency" 
						:options="[{ _id: 'daily', label: 'Daily' },{ _id: 'weekly', label: 'Weekly' },{ _id: 'monthly', label: 'Monthly' }]" 
						:reduce="option => option._id"
						:append-to-body="true"
						:clearable="false"
					/>

					<hr v-if="!currentFrequency&&showKeywordInput" />

					<label v-if="!currentFrequency&&showKeywordInput">Keyword(s)</label>
					<p class="info" v-html="keywordsLabel" v-if="!currentFrequency&&showKeywordInput&&keywordsLabel" />
					<FormInput 
						type="textarea" 
						v-model="kw"
						name="keywords" 
						placeholder="One per line..."
						v-if="!currentFrequency&&showKeywordInput"
						theRef="keywords"
						id="keywords"
						:autoResize="true"
						:required="required"
						:autoResizeMaxHeight="160"
						:noMarginBottom="!showProjects"
						:focus="focus"
						:heightBuffer="heightBuffer"
					/>

					<hr v-if="projectListProcessed.length>1&&currentFrequency!='multi'&&showProjects" />

					<div class="select-wrap" v-if="projectListProcessed.length>1&&currentFrequency!='multi'&&showProjects">

						<label>Projects</label>
						<p class="info" v-if="initProjects===0">You can assign to multiple projects at the same time if you need to.</p>
						
						<p class="info" v-if="initProjects>1">We've pre-selected all projects for this domain, remove any you don't want to add the keyword(s) to.</p>
						
						<v-select 
							multiple 
							v-model="projects" 
							:options="projectListProcessed" 
							:append-to-body="true"
							:reduce="option => option._id"
							label="name"
						/>

					</div>

					<hr v-if="currentFrequency!='multi'&&showProjects" />

					<TagsPreview 
						:tags="tags"
						:availableTags="availableTags"
						@save="saveTags"
						v-if="currentFrequency!='multi'&&showProjects"
					/>

					<p v-if="success!==''" class="success" v-html="success" />

					<p v-if="error!==''" class="error" v-html="error" />

					<div class="large-button-group">
					
						<span class="large-button" @click="submit" v-if="(keywords!==''||currentFrequency)&&!emitChanges" v-html="currentFrequency ? 'Update' : 'Add'" />

						<span :class="{'large-button':true,disabled:!frequency||disableCSVupload}" @click="showTheUploader" v-html="'Upload CSV'" v-if="csvUpload&&!currentFrequency" />
						
					</div>
				</form>

				<div v-if="csvUploaded&&!confirmed">

					<FormInput 
						type="number" 
						v-model="headerRow"  
						name="headerRow" 
						placeholder="Enter New Heading Row"
						:required="false"
						min="2"
						v-if="showRetry"
					/>

					<span v-if="headerRow>1||!showRetry" :class="{'large-button':true,'heading-btn':true,retry:!!showRetry}" @click="retry" v-html="showRetry?'Retry':'Change Heading Row'" />

					<p class="warning" v-if="!csvHeadingsProcessed.length">
						We couldn't find any headings on the {{headerRowText}} row?
					</p>

					<div v-if="csvHeadingsProcessed.length">

						<h2 class="heading">Select Your Columns</h2>

						<p v-if="!headerRow&&csvHeadingsProcessed.length">
							We have assumed that the headings are in the first row, if they are not please change the row number above and hit retry.
						</p>

						<p>
							You can either select the "Keyword Column" only to import your keywords and start tracking them from today or select all 4 options to pull in historic data<span v-if="hasProject">, if the keywords already exist in the project they will be ignored</span>.
						</p>

						<div class="select-wrap">

							<label>Keyword Column</label>
							<v-select 
								v-model="csvUploadSelectedHeadings.keyword" 
								:options="csvHeadingsProcessed" 
								:append-to-body="true"
								:reduce="option => option.index"
							/>

						</div>
						<div class="select-wrap">

							<label>Position Column</label>
							<p class="warning">This should be a number only!</p>
							<v-select 
								v-model="csvUploadSelectedHeadings.position" 
								:options="csvHeadingsProcessed" 
								:append-to-body="true"
								:reduce="option => option.index"
							/>

						</div>
						<div class="select-wrap">

							<label>Date Column</label>
							<p class="warning">The format should be: <strong>MM/DD/YYYY</strong> or <strong>MM/DD/YYYY HH:MM</strong> or <strong>MM/DD/YYYY HH:MM:SS</strong> or <strong>YYYY-MM-DD HH:MM:SS</strong>. <br>If the dates you upload are in UK format (DD/MM/YYYY HH:MM) this will cause an incorrect result as the system will assume MM/DD/YYYY HH:MM, 08/06/2021 18:21 will be processsed as August 6th 2021 @ 18:21, 21/06/2021 18:21 will cause an error and will be ignored, please check your dates are correct and re-format them in Excel/Numbers if needed (this can be done in a few clicks).</p>
							<v-select 
								v-model="csvUploadSelectedHeadings.date" 
								:options="csvHeadingsProcessed" 
								:append-to-body="true"
								:reduce="option => option.index"
							/>
						</div>
						<div class="select-wrap">

							<label>URL Column</label>
							<v-select 
								v-model="csvUploadSelectedHeadings.url" 
								:options="csvHeadingsProcessed" 
								:append-to-body="true"
								:reduce="option => option.index"
							/>

						</div>

						<span :class="{'large-button':true,disabled:!showImportData,pulse:showImportData}" @click="importCSVRows" v-html="'Import Data'" />

					</div>

				</div>

			</template>

		</Modal>

		<Uploader 
			v-if="showUploader" 
			:multiple="false" 
			@close-uploader="closeUploader"
			title="CSV Upload"
			:fileTypes="['csv']"
			:formData="uploadFormData"
		/>

	</div>

</template>

<script>
import Vue from 'vue';
import { mapMutations, mapState } from 'vuex'
import ApiService from './../services/api.service'
import general from './../utils/general'
import FormInput from './Form/Input.vue'
import Modal from './Display/Modal.vue'
import Uploader from './Uploader.vue'
import Loading from './Loading.vue'
import TagsPreview from './TagsPreview.vue'
export default {
	name: 'Keywords',
	props: {
		title: {
			type: String,
			default: 'Track Keywords'
		},
		currentFrequency: String,
		id: {
			type: [String,Array],
			default: ''
		},
		project: {
			type: [String,Array],
			default: ''
		},
		keywords: {
			type: Array,
			default: function () { return [] }
		},
		meta: {
			type: Object,
			default: function () { return {} }
		},
		instantUpload: {
			type: Boolean,
			default: false
		},
		csvUpload: {
			type: Boolean,
			default: false
		},
		disableCSVupload: {
			type: Boolean,
			default: false
		},
		showCSVhint: {
			type: Boolean,
			default: true
		},
		internal: {
			type: Boolean,
			default: false
		},
		noModal: {
			type: Boolean,
			default: false
		},
		showProjects: {
			type: Boolean,
			default: true
		},
		emitChanges: {
			type: Boolean,
			default: false
		},
		showKeywordInput: {
			type: Boolean,
			default: true
		},
		frequencyLabelOverride : {
			type: String,
			default: ''
		},
		keywordsLabelOverride : {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: true
		},
		focus: {
			type: Boolean,
			default: false
		},
		autoResizeMaxHeight: {
			type: Number,
			default: 160
		},
		heightBuffer: {
			type: Number,
			default: 0
		}
	},
	components: {
		FormInput,
		Uploader,
		Loading,
		TagsPreview,
		Modal
	},
	data() {

		return {
			kw: '',
			frequency: 'daily',
			csvHeadings: [],
			csvUploadSelectedHeadings: {},
			headerRow: '',
			showUploader: false,
			loading: '',
			csvId: null,
			showRetry: false,
			confirmed: false,
			error: '',
			success: '',
			tags: [],
			availableTags: [],
			projects: [],
			initProjects: 0
		}

	},
	watch: {
		projects: function () {
			
			this.getTags();
			this.error = '';
			this.submitChanged ();

		},
		frequency: function () {

			this.submitChanged ();

		},
		kw: function () {

			this.submitChanged ();

		},
		showKeywordInput: function () {

			this.error = '';

		},
		csvId: function () {

			this.submitChanged ();

		},
		confirmed: function () {

			this.submitChanged ();

		}
	},
	computed: { 

		...mapState('general',['projectList']),

		headerRowText () {

			let n = this.headerRow || 1;

			return n + general.ordinal(n);

		},

		projectListProcessed () {

			return this.projectList.filter( function (p) {

				return !!p.hasRankTracking;

			});

		},

		frequencyLabel () {

			if ( this.frequencyLabelOverride ) return this.frequencyLabelOverride;

			let m = '';

			if ( !this.currentFrequency ) {

				m = 'How often should we check these keywords?';

				if ( this.showCSVhint ) {

					m += ' (if you import from a CSV this frequency will be used when adding new keywords)';

				}

			}
			else if ( this.currentFrequency != 'multi' ) {

				m = 'Update how often should we check these keywords?';

				if ( this.showCSVhint ) {

					m += ' (if you import from a CSV this frequency will be used when adding new keywords)';

				}

			}

			return m;

		},

		keywordsLabel () {

			if ( this.keywordsLabelOverride ) return this.keywordsLabelOverride;

			return '';

		},

		hasProject () {

			if ( Array.isArray( this.project ) ) return this.project.length > 0;

			return this.project !== '';

		},

		uploadFormData () {

			let f = { type:'csv', vars:{ headerRow: this.headerRow } };

			if ( this.hasProject ) f.vars.project = this.project;
			
			return f;

		},
		
		showTitle () {

			if ( this.noModal ) return '';

			if ( this.csvUploaded ) return "CSV Upload";

			return this.title ? this.title : 'Add/Import Keywords';

		},
		csvUploaded () {

			if ( this.csvHeadings.length > 0 ) return true;

			return false;

		},
		showImportData () {

			if ( typeof this.csvUploadSelectedHeadings.keyword === 'undefined' || this.csvUploadSelectedHeadings.keyword === null ) return false;

			let c = 0;

			Object.keys(this.csvUploadSelectedHeadings).forEach ( function (nn) {

				if ( this.csvUploadSelectedHeadings[nn] !== null ) ++c;

			}.bind(this));

			return c === 1 || c === 4;

		},
		csvHeadingsProcessed () {

			let r = [];

			this.csvHeadings.forEach( function ( v, i ) {

				if ( v ) r.push( { label: v, index: i } )

			}.bind(this));

			return r;

		}

    },
	methods: {

		...mapMutations('general',['updateLoadingText']),

		saveTags (info) {

			this.tags = info;

		},

		showTheUploader () {

			if ( !this.frequency || this.disableCSVupload ) return;
			this.showUploader = true;
			this.error = '';

		},

		importCSVRows () {

			if ( !this.showImportData ) return;

			this.loading = 'One moment...';
			this.error = "";

			ApiService.put( 'keywords/csv-confirm/' + this.csvId, { headingInfo: this.csvUploadSelectedHeadings, frequency: this.frequency } )
			.then( function( info ) {

				this.loading = '';
				this.confirmed = true;

			}.bind(this))
			.catch( function(err) {

				this.loading = '';
				this.$snotify.error("There was an error processing your CSV", "Import CSV Data");
				this.error = "There was an error processing your CSV";
				this.showRetry = false;

			}.bind(this));

		},

		updateDefaultHeadingSelections () {

			let settings = [
					{ field: 'keyword', headings: ['keyword','keywords'] },
					{ field: 'position', headings: ['position','rank','base rank'] },
					{ field: 'date', headings: ['date'] },
					{ field: 'url', headings: ['url'] }
				]

			this.csvHeadings.forEach ( function (h,i) {

				settings.forEach ( function (setting) {
					
					if ( setting.headings.indexOf(h.toLowerCase()) >= 0 ) {

						Vue.set( this.csvUploadSelectedHeadings, setting.field, i );

					}

				}.bind(this));

			}.bind(this));

		},

		retry() {

			if ( !this.showRetry ) {

				this.showRetry = true;
				return;

			}

			this.loading = 'One moment...';
			this.error = "";

			ApiService.get( 'keywords/csv-retry/'+this.csvId+'/' + this.headerRow )
			.then( function( info ) {

				this.loading = '';
				this.csvHeadings = info.data;
				Vue.set(this,'csvUploadSelectedHeadings',{});
				this.updateDefaultHeadingSelections ();
				this.showRetry = false;
				if ( this.csvHeadings.length ) {
					this.$snotify.success("Please check the dropdowns for your new headings...", "Retry CSV");
				}
				else {
					this.$snotify.error("We couldn't find any headings on the " + this.headerRowText + " row?", "Retry CSV");
				}

			}.bind(this))
			.catch( function(err) {

				this.loading = '';
				this.$snotify.error("There was an error processing your CSV", "Retry CSV");
				this.error = "There was an error processing your CSV";
				this.showRetry = false;

			}.bind(this));

		},

		closeUploader (info) {

			this.showUploader = false;

			general.modalClose();
			
			if ( typeof info !== 'undefined' ) {

				if ( info.error ) {

					this.$snotify.error( info.error, 'CSV Upload' );

					this.error = 'CSV ERROR: ' + info.error;

				}
				else if ( info !== null && typeof info.headings !== 'undefined' ) {

					this.csvHeadings = info.headings;
					this.csvId = info._id;
					this.updateDefaultHeadingSelections ();

				}

			}

		},

		displayError(error) {

			this.errors = error ? error.message : '';

		},

		submitChanged () {

			if ( !this.emitChanges ) return;

			let info = { 
				keywords: this.kw, 
				frequency: this.frequency, 
				csvId: this.csvId, 
				csvConfirmed: this.confirmed 
			};

			this.$emit( 'changed', info );

		},

		submit() {

			if ( this.projects.length === 0 ) {

				this.error = "Please select at least 1 project";
				return;

			}

			let info = this.currentFrequency ? { _id: this.id, frequency: this.frequency, tags: this.tags, projects: this.projects, title: this.title } : { keywords: this.kw, frequency: this.frequency, tags: this.tags, projects: this.projects };

			if ( this.internal ) {
				
				let s = info.keywords.trim().split(/\r\n|\r|\n/).length === 1 ? '' : 's';

				info.meta = this.meta;

				this.updateLoadingText('Adding keyword'+s+'...');

				ApiService.post( 'keywords/' + info.projects.join(','), info )
				.then( function( response ) {
					
					this.updateLoadingText();

					let projects = {},
						results = { 
							error: {}, 
							success: {}
						};

					response.data.forEach( function (n) {

						projects[n.projectId] = n.projectName || 'Unknown Project';

						let type = n.error ? 'error' : 'success',
							type2 = n.errorType || 'success';

						results[type][type2] = results[type][type2] || [];

						results[type][type2][n.projectId] = results[type][type2][n.projectId] || [];

						if ( n.keyword ) results[type][type2][n.projectId].push( n.keyword );

					});

					if ( Object.keys( results.error ).length > 0 ) {

						if ( Object.keys( results.success ).length > 0 ) {

							let successMsgs = ['<strong>The following keywords were added</strong>'];

							 Object.keys( results.success.success ).forEach( function (projectId) {

							 	 successMsgs.push( '<em>' + projects[projectId] + "</em>: " + results.success.success[projectId].join(", ") );

							}.bind(this));

							this.success = successMsgs.join('<br />');

						}

						let errorMsgs = [];

						Object.keys( results.error ).forEach( function (errorType) {

							if ( errorMsgs.length > 0 ) errorMsgs.push('');

							switch ( errorType ) {

								case 'colon':

									errorMsgs.push('<strong>The following keywords could not be added because they contain a colon (:)</strong>');

								break;

								case 'exists':

									errorMsgs.push('<strong>The following keywords already exist</strong>');

								break;

								case 'project-not-found':

									errorMsgs.push('<strong>The following projects could not be found</strong>');

								break;

							}

							Object.keys( results.error[errorType] ).forEach( function (projectId) {

								let m = '<em>' + projects[projectId] + "</em>";

								if ( results.error[errorType][projectId].length ) m += ": " + results.error[errorType][projectId].join(", ");

								errorMsgs.push( m );

							}.bind(this));

						}.bind(this));

						this.error = errorMsgs.join('<br />');

						setTimeout( function () {

							let objDiv = this.$refs['kw-scroller'];
							objDiv.scrollTop = objDiv.scrollHeight;

						}.bind(this), 30 );

					}
					else {

						let count = info.keywords.trim().split('\n').length, msg;

						if ( count > 1 ) {

							msg = count + " keywords were added successfully.";

						}
						else {

							msg = info.keywords + " was added successfully.";

						}

						this.$snotify.success( msg, 'Track Keywords');

						this.$emit('close',info);

					}

				}.bind(this))
				.catch( function(err) {

					this.updateLoadingText();
					this.$snotify.error("Your keyword" + s + " could not be added, do you have permission to do this?", 'Add Keyword'+s);

				}.bind(this));

			}
			else {

				this.$emit('close',info);

			}

		},

		close() {

			this.$emit('close');

		},

		getTags () {

			if ( this.projects.length > 0 ) {

				ApiService.get( 'keywords/tags/' + this.projects.join(',') )
				.then( function( info ) {

					this.availableTags = info.data;

				}.bind(this))
				.catch( function(err) {

					

				}.bind(this));

			}
			else {

				this.availableTags = [];

			}

		}
	},
	mounted() {

		this.frequency = ( this.currentFrequency && this.currentFrequency !== 'multi' ) ? this.currentFrequency : 'daily';
		this.kw = this.keywords.length ? ( this.keywords.join('\n').trim() + '\n' ) : '';
		if ( this.instantUpload ) this.showTheUploader();
		if ( this.project ) {

			let p = Array.isArray( this.project ) ? this.project : [this.project], 
				projects = [];

			p.forEach( function (p) {

				const pp = this.projectListProcessed.find( pr => pr._id.toString() === p.toString() );

				if ( pp ) projects.push( p );

			}.bind(this));

			this.projects = projects;
			this.initProjects = this.projects.length;
			this.getTags();
			
		}

	}
}
</script>
<style lang="less" scoped>
.heading-btn {
	margin: 0 0 20px;
}
.pulse {
	animation: pulse 1.5s infinite;
}
@keyframes pulse {
	0% { transform: scale(1); }
	50% { transform: scale(1.1); }
	100% { transform: scale(1); }
}
</style>